import { Box, Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import useBreakPoinst from "@hooks/useBreakPoinst";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  ModalBox,
  ModalTitle,
  ModalDescription,
  Divider,
  ActionContainer,
  StyledButton,
  CloseButtonContainer,
  TitleSection,
  FoundText,
  SelectAllBox,
  PropertyGrid,
  PropertyWrapper,
} from "./styles/AirbnbChooseProperties";

import CloseMenu from "../../../../assets/svg/closeMenu.svg?react";
import PropertyCard from "./PropertyCard";
import PropertyCardSkeleton from "./PropertyCardSkeleton";
import { useProperty } from "@services";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import { LoadingWrapper } from "@components";

interface ConnectAirbnbModalProps {
  open: boolean;
  onClose: () => void;
  handleNextModal?: () => void;
  onRefetchOnboarding?: () => void;
}

interface FormData {
  selectedProperties: string[];
}

const AirbnbChooseProperties = ({
  open,
  onClose,
  handleNextModal,
  onRefetchOnboarding,
}: ConnectAirbnbModalProps) => {
  const { isPhone } = useBreakPoinst();
  const { t } = useTranslation();
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const { showError } = useContext(ToastMessageContext);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      selectedProperties: [],
    },
    mode: "onChange",
  });

  const { getAirbnbListingQuery, addPropertyMutate } = useProperty();
  const channexChannelId = localStorage.getItem("channexChannelId");
  const {
    data: propertyList,
    isLoading,
    refetch,
    isRefetching,
  } = getAirbnbListingQuery(channexChannelId || '');

  const list =
    propertyList?.response?.data?.listing_id_dictionary?.values || [];

  const handlePropertySelect = (propertyId: string) => {
    const newSelectedProperties = selectedProperties.includes(propertyId)
      ? selectedProperties.filter((id) => id !== propertyId)
      : [...selectedProperties, propertyId];

    setSelectedProperties(newSelectedProperties);
    setValue("selectedProperties", newSelectedProperties);
  };

  const handleSelectAll = (checked: boolean) => {
    const unconnectedProperties = list.filter(
      (property: any) => !property.isConncted
    );
    const allPropertyIds = unconnectedProperties.map(
      (property: any) => property.id
    );
    const newSelectedProperties = checked ? allPropertyIds : [];

    setSelectedProperties(newSelectedProperties);
    setValue("selectedProperties", newSelectedProperties);
  };

  const onSubmit = (data: FormData) => {
    if (data.selectedProperties.length > 0) {
      const selectedPropertyData = list.filter((property: any) =>
        data.selectedProperties.includes(property.id)
      );

      const payload: any = selectedPropertyData.map((property: any) => ({
        propertyLocation: property?.city,
        propertyId: 0,
        propertyName: property?.title,
        propertyIsDeteled: false,
        propertyCreateDate: null,
        gathrenUnitId: "",
        airbnbUnitId: `${property?.id}`,
        autoAccess: false,
        status: true,
        templateId: null,
        propertyImage: property?.image,
      }));

      addProperty(payload);
    }
  };

  const validateSelectedProperties = (value: string[]) => {
    return value.length > 0 || "Please select at least one property";
  };

  register("selectedProperties", {
    validate: validateSelectedProperties,
  });

  const {
    mutate: addProperty,
    isSuccess,
    isPending,
    reset,
  } = addPropertyMutate({});

  useEffect(() => {
    reset();
    if (channexChannelId && channexChannelId !== 'null' && channexChannelId !== 'undefined') {
      refetch();
    }
  }, []);

  useEffect(() => {
    if (isSuccess) {
      // Revalidate main onboarding query AFTER API is fulfilled
      onRefetchOnboarding?.();
      handleNextModal?.();
      reset();
    }
  }, [isSuccess, handleNextModal, reset, onRefetchOnboarding]);

  // Fallback logic for "no properties found"
  const showNoPropertiesFound =
    (!isLoading && !isRefetching && (list.length === 0 || !propertyList)) ||
    (!isLoading &&
      !isRefetching &&
      (propertyList === null || propertyList === undefined));

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="connect-airbnb-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoadingWrapper loading={false}>
        <ModalBox $isPhone={isPhone}>
          <CloseButtonContainer onClick={onClose}>
            <CloseMenu />
          </CloseButtonContainer>

          <TitleSection>
            <ModalTitle $isPhone={isPhone}>
              {t("onBoardingJourny.choosePropertiesTitle")}
            </ModalTitle>
            <ModalDescription $isPhone={isPhone}>
              {t("onBoardingJourny.choosePropertiesDescription")}
            </ModalDescription>
          </TitleSection>

          <FoundText>
            {t("onBoardingJourny.foundProperties", {
              count: list?.filter((property: any) => !property.isConncted)
                .length,
            })}
          </FoundText>

          <SelectAllBox>
            <label htmlFor="checkbox">
              {t("onBoardingJourny.selectAll", {
                count: list?.filter((property: any) => !property?.isConncted)
                  ?.length,
              })}
            </label>
            <input
              type="checkbox"
              checked={
                selectedProperties.length ===
                  list?.filter((property: any) => !property.isConncted)
                    .length &&
                list?.filter((property: any) => !property.isConncted).length > 0
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
              disabled={isLoading}
            />
          </SelectAllBox>

          <PropertyGrid>
            {isLoading || isRefetching ? (
              // Show skeleton loaders while loading
              Array.from({ length: isPhone ? 2 : 8 }).map((_, index) => (
                <PropertyWrapper key={`skeleton-${index}`}>
                  <PropertyCardSkeleton />
                </PropertyWrapper>
              ))
            ) : showNoPropertiesFound ? (
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  textAlign: "start",
                  color: "#888",
                  fontSize: "16px",
                  // padding: "32px 0",
                }}
              >
                {t("onBoardingJourny.noPropertiesFound")}
              </div>
            ) : (
              // Show actual property cards when loaded (filter out connected properties)
              <Box sx={{ maxWidth: "650px" }}>
                {list
                  .filter((property: any) => !property.isConncted)
                  .map((property: any, index: number) => (
                    <PropertyWrapper key={property.id || index}>
                      <PropertyCard
                        title={property.title}
                        address={`${property.street}, ${property.city}`}
                        image={property.image}
                        isSelected={selectedProperties.includes(property.id)}
                        onSelect={() => handlePropertySelect(property.id)}
                        isSyncing={
                          isPending && selectedProperties.includes(property.id)
                        }
                      />
                    </PropertyWrapper>
                  ))}
              </Box>
            )}
          </PropertyGrid>

          {errors.selectedProperties && (
            <div
              style={{
                color: "red",
                fontSize: "12px",
                marginTop: "8px",
                textAlign: "center",
              }}
            >
              {t("onBoardingJourny.selectAtLeastOneProperty")}
            </div>
          )}

          <Divider />

          <ActionContainer $isPhone={isPhone}>
            <StyledButton
              onClick={handleSubmit(onSubmit)}
              $TextColor="white"
              $BgColor="#5B3FFF"
              $isPhone={isPhone}
              $width="127px"
              disabled={isLoading || isPending}
            >
              {isPending
                ? "جاري الربط..."
                : t("onBoardingJourny.connectAccount")}
            </StyledButton>

            <StyledButton
              $TextColor="#5B3FFF"
              $isPhone={isPhone}
              $BgColor="white"
              $width="85px"
              onClick={onClose}
              disabled={isLoading || isPending}
            >
              {t("onBoardingJourny.skip")}
            </StyledButton>
          </ActionContainer>
        </ModalBox>
      </LoadingWrapper>
    </Modal>
  );
};

export default AirbnbChooseProperties;
